import { jsPDF } from "jspdf";
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  DollarSign,
  FileText,
  Home,
  User,
  X
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdvancedRoofBidderHeader from "../../components/AdvancedRoofBidderHeader";
import { useAuth } from "../../contexts/AuthContext";
import { supabase, supabaseHelpers } from "../../lib/supabase";

const OrderHistoryPage = () => {
  const navigate = useNavigate();
  const { user, session } = useAuth();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pdfViewUrl, setPdfViewUrl] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);

  // Fetch orders for current user
  const fetchOrders = async () => {
    try {
      setLoading(true);
      let fetchedOrders = [];
      if (supabase) {
        fetchedOrders = await supabaseHelpers.getMyOrders();
      } else {
        // Demo mode: filter localStorage by email/id if available
        const all = await supabaseHelpers.getAllOrders();
        fetchedOrders = all.filter(
          (o) => !user || o.user_email === user?.email || o.user_id === user?.id
        );
      }

      const transformedOrders = fetchedOrders.map((order) => ({
        id: order.id,
        date: order.order_date,
        status: order.status,
        contractor: order.contractor_name,
        contractorContact: order.contractor_contact,
        contractorPhone: order.contractor_phone,
        contractorEmail: order.contractor_email,
        totalCost: order.total_cost,
        timeline: order.timeline,
        warranty: order.warranty,
        materials: order.materials,
        scheduledDate: order.scheduled_date,
        completionDate: order.completion_date,
        homeowner: {
          name: order.homeowner_name,
          email: order.homeowner_email,
          phone: order.homeowner_phone,
          address: order.property_address,
          propertyType: order.property_type,
          projectType: order.project_type,
        },
        measurements: order.roof_measurements || {
          totalArea: 0,
          pitchedArea: 0,
          flatArea: 0,
          roofType: "N/A",
          pitch: "N/A",
          starter: 0,
          caps: 0,
          valley: 0,
          ridges: 0,
          dripEdge: 0,
          perimeter: 0,
        },
      }));

      setOrders(transformedOrders);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   fetchOrders();
  // }, [user]);

  // // Initial fetch & multi-tab session handling
  // useEffect(() => {
  //   fetchOrders(user);

  //   const handleStorageChange = (event) => {
  //     if (event.key === "supabase.auth.token") {
  //       supabase.auth.getSession().then(({ data: { session: newSession } }) => {
  //         const newUser = newSession?.user ?? null;
  //         fetchOrders(newUser); // refresh orders if user changed
  //       });
  //     }
  //   };

  //   window.addEventListener("storage", handleStorageChange);
  //   return () => window.removeEventListener("storage", handleStorageChange);
  // }, [user]);

  // Generate PDF for order with professional design

  //   useEffect(() => {
  //   // Only fetch if user is defined
  //   const fetch = async () => {
  //     if (!user) {
  //       setOrders([]);
  //       setLoading(false);
  //       return;
  //     }
  //     setLoading(true);
  //     try {
  //       await fetchOrders();
  //     } catch (err) {
  //       console.error("Error fetching orders:", err);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetch();
  // }, [user, session]);

  // useEffect(() => {
  //   const handleStorageChange = async (event) => {
  //     if (event.key === "supabase.auth.token") {
  //       try {
  //         const { data: { session: newSession } } = await supabase.auth.getSession();
  //         const newUser = newSession?.user ?? null;

  //         // Only refetch if user changed
  //         if (newUser?.id !== user?.id) {
  //           setLoading(true);
  //           await fetchOrders();
  //           setLoading(false);
  //         }
  //       } catch (err) {
  //         console.error("Error refreshing session on storage change:", err);
  //       }
  //     }
  //   };

  //   window.addEventListener("storage", handleStorageChange);
  //   return () => window.removeEventListener("storage", handleStorageChange);
  // }, [user]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (!user) {
          setOrders([]);
          return;
        }
        await fetchOrders();
      } catch (err) {
        console.error("Error fetching orders:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Listen for tab visibility changes
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible") {
        // Refresh session from Supabase
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const newUser = session?.user ?? null;

        // If the user changed, refetch orders
        if (newUser?.id !== user?.id) {
          await fetchData();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [user]);

  const generateOrderPDF = (order, viewOnly = false) => {
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Colors
    const primaryColor = [153, 27, 51];
    const accentColor = [220, 53, 69];
    const darkGray = [51, 51, 51];

    // Right stripe
    doc.setFillColor(...primaryColor);
    doc.rect(pageWidth - 10, 0, 10, pageHeight, "F");

    // Header bar
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth - 10, 35, "F");

    // Title
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.text("ROOFING JOB ESTIMATE", 40, 15);

    // Reset text color
    doc.setTextColor(...darkGray);

    // Body (trimmed for brevity) - same as before
    let yPos = 45;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text(`Estimate Number : ${order.id}`, 15, yPos);
    doc.text(
      `Date : ${new Date(order.date).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })}`,
      15,
      yPos + 6
    );

    return doc.output("bloburl");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "bg-emerald-100 text-emerald-800";
      case "in-progress":
        return "bg-yellow-100 text-yellow-800";
      case "pending":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-5 h-5" />;
      case "in-progress":
        return <Clock className="w-5 h-5" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  return (
    <AdvancedRoofBidderHeader>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Loading State */}
          {loading && orders?.length < 1 && (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
            </div>
          )}

          {/* Orders Content */}
          {orders?.length > 0 && (
            <>
              {/* Header */}
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => navigate("/")}
                    className="p-2 hover:bg-white rounded-lg transition-colors"
                  >
                    <ArrowLeft className="w-6 h-6 text-gray-600" />
                  </button>
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900">
                      My Orders
                    </h1>
                    <p className="text-gray-600">
                      View your roofing project orders
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="bg-white px-6 py-3 rounded-xl shadow-sm border border-gray-200">
                    <div className="text-sm text-gray-600">Total Orders</div>
                    <div className="text-2xl font-bold text-orange-600">
                      {orders.length}
                    </div>
                  </div>
                </div>
              </div>

              {/* Orders List */}
              <div className="space-y-6">
                {orders.map((order) => (
                  <div
                    key={order.id}
                    className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow"
                  >
                    {/* Order Header */}
                    <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <FileText className="w-8 h-8 text-white" />
                          <div>
                            <h3 className="text-xl font-bold text-white">
                              Order #{order.id}
                            </h3>
                            <p className="text-orange-100 text-sm">
                              {new Date(order.date).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                }
                              )}
                            </p>
                          </div>
                        </div>
                        <div
                          className={`flex items-center space-x-2 px-4 py-2 rounded-full font-semibold ${getStatusColor(
                            order.status
                          )}`}
                        >
                          {getStatusIcon(order.status)}
                          <span className="capitalize">
                            {order.status.replace("-", " ")}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Order Content */}
                    <div className="p-6">
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                        {/* Property Info */}
                        <div className="space-y-3">
                          <h4 className="font-semibold text-gray-900 flex items-center">
                            <Home className="w-5 h-5 mr-2 text-orange-600" />
                            Property Details
                          </h4>
                          <div className="text-sm space-y-2">
                            <div>
                              <span className="text-gray-600">Address:</span>
                              <p className="font-medium text-gray-900">
                                {order.homeowner.address}
                              </p>
                            </div>
                            <div>
                              <span className="text-gray-600">Type:</span>
                              <p className="font-medium text-gray-900">
                                {order.homeowner.propertyType}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Contractor Info */}
                        <div className="space-y-3">
                          <h4 className="font-semibold text-gray-900 flex items-center">
                            <User className="w-5 h-5 mr-2 text-blue-600" />
                            Contractor
                          </h4>
                          <div className="text-sm space-y-2">
                            <div>
                              <span className="text-gray-600">Company:</span>
                              <p className="font-medium text-gray-900">
                                {order.contractor}
                              </p>
                            </div>
                            <div>
                              <span className="text-gray-600">Contact:</span>
                              <p className="font-medium text-gray-900">
                                {order.contractorContact}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Project Info */}
                        <div className="space-y-3">
                          <h4 className="font-semibold text-gray-900 flex items-center">
                            <DollarSign className="w-5 h-5 mr-2 text-emerald-600" />
                            Project Info
                          </h4>
                          <div className="text-sm space-y-2">
                            <div>
                              <span className="text-gray-600">Total Cost:</span>
                              <p className="font-bold text-emerald-600 text-lg">
                                ${order.totalCost.toLocaleString()}
                              </p>
                            </div>
                            <div>
                              <span className="text-gray-600">Timeline:</span>
                              <p className="font-medium text-gray-900">
                                {order.timeline}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Roof Measurements Summary */}
                      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 mb-6">
                        <h4 className="font-semibold text-gray-900 mb-3">
                          Roof Measurements
                        </h4>
                        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                          <div className="text-center">
                            <div className="text-xs text-gray-600">
                              Total Area
                            </div>
                            <div className="font-bold text-orange-600">
                              {order.measurements.totalArea}
                            </div>
                            <div className="text-xs text-gray-500">sq ft</div>
                          </div>
                          <div className="text-center">
                            <div className="text-xs text-gray-600">Pitched</div>
                            <div className="font-bold text-blue-600">
                              {order.measurements.pitchedArea}
                            </div>
                            <div className="text-xs text-gray-500">sq ft</div>
                          </div>
                          <div className="text-center">
                            <div className="text-xs text-gray-600">Flat</div>
                            <div className="font-bold text-green-600">
                              {order.measurements.flatArea}
                            </div>
                            <div className="text-xs text-gray-500">sq ft</div>
                          </div>
                          <div className="text-center">
                            <div className="text-xs text-gray-600">Pitch</div>
                            <div className="font-bold text-purple-600">
                              {order.measurements.pitch}
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-xs text-gray-600">Type</div>
                            <div className="font-bold text-indigo-600 text-xs">
                              {order.measurements.roofType}
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-xs text-gray-600">
                              Perimeter
                            </div>
                            <div className="font-bold text-teal-600">
                              {order.measurements.perimeter}
                            </div>
                            <div className="text-xs text-gray-500">ft</div>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-3">
                        <button
                          onClick={() =>
                            setSelectedOrder(
                              order.id === selectedOrder ? null : order.id
                            )
                          }
                          className="flex-1 md:flex-none bg-gradient-to-r from-gray-600 to-gray-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-gray-700 hover:to-gray-800 transition-all duration-300 flex items-center justify-center space-x-2"
                        >
                          <FileText className="w-5 h-5" />
                          <span>
                            {selectedOrder === order.id
                              ? "Hide Details"
                              : "View Details"}
                          </span>
                        </button>

                        {/* NEW: Download Button */}
                        {/* <button
                          onClick={() => {
                            const pdfUrl = generateOrderPDF(order, false);
                            const link = document.createElement("a");
                            link.href = pdfUrl;
                            link.download = `Order-${order.id}.pdf`;
                            link.click();
                          }}
                          className="flex-1 md:flex-none bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-green-700 hover:to-green-800 transition-all flex items-center justify-center space-x-2"
                        >
                          <Download className="w-5 h-5" />
                          <span>Download PDF</span>
                        </button> */}

                        {/* NEW: View Button */}
                        {/* <button
                          onClick={() => {
                            const url = generateOrderPDF(order, true);
                            setPdfViewUrl(url);
                            setShowPdfModal(true);
                          }}
                          className="flex-1 md:flex-none bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 transition-all flex items-center justify-center space-x-2"
                        >
                          <Eye className="w-5 h-5" />
                          <span>View PDF</span>
                        </button> */}
                      </div>

                      {/* Expanded Details */}
                      {selectedOrder === order.id && (
                        <div className="mt-6 pt-6 border-t border-gray-200">
                          <h4 className="font-semibold text-gray-900 mb-4">
                            Complete Measurements
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            {Object.entries({
                              "Starter Shingles": `${order.measurements.starter} ft`,
                              "Ridge Caps": `${order.measurements.caps} ft`,
                              Valley: `${order.measurements.valley} ft`,
                              Ridges: `${order.measurements.ridges} ft`,
                              "Drip Edge": `${order.measurements.dripEdge} ft`,
                              Perimeter: `${order.measurements.perimeter} ft`,
                            }).map(([label, value]) => (
                              <div
                                key={label}
                                className="bg-gray-50 p-3 rounded-lg border border-gray-200"
                              >
                                <div className="text-xs text-gray-600 mb-1">
                                  {label}
                                </div>
                                <div className="font-bold text-gray-900">
                                  {value}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Empty State */}
              {orders.length === 0 && (
                <div className="text-center py-16">
                  <FileText className="w-20 h-20 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">
                    No Orders Yet
                  </h3>
                  <p className="text-gray-500 mb-6">
                    Place an order to see it here
                  </p>
                  <button
                    onClick={() => navigate("/")}
                    className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 transition-all"
                  >
                    Get Started
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* PDF VIEW MODAL */}
      {showPdfModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-3xl rounded-xl shadow-xl relative">
            <button
              onClick={() => setShowPdfModal(false)}
              className="absolute top-3 right-3 p-2 bg-gray-200 rounded-full hover:bg-gray-300"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="p-4 border-b">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center space-x-2">
                <FileText className="w-5 h-5" />
                <span>Order Preview</span>
              </h2>
            </div>

            <iframe
              src={pdfViewUrl}
              className="w-full h-[600px] rounded-b-xl"
              title="Order PDF Preview"
            ></iframe>
          </div>
        </div>
      )}
    </AdvancedRoofBidderHeader>
  );
};

export default OrderHistoryPage;
